// Gumroad bundle promotion code — Account tab UI.
// Owns: eligibility view states, request CTA, existing code display, help panel, admin preview (H key).
// Does NOT own: Gumroad API, Stripe promo creation, or Mongo (AccountManager → GumroadManager routes).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RevoltButton } from '../../../components';
import { useManager } from '../../../core';
import { AccountManager } from '../managers/account-manager';
import {
  GUMROAD_BUNDLE_ELIGIBILITY_PATH,
  GUMROAD_BUNDLE_HELP_CONTEXT,
  GUMROAD_BUNDLE_ISSUANCE_PENDING_INTRO,
  GUMROAD_BUNDLE_NOT_ELIGIBLE_INTRO,
  GUMROAD_BUNDLE_PANEL_COPY,
  GUMROAD_BUNDLE_REQUEST_ERROR_MESSAGES,
  GUMROAD_BUNDLE_UI_STATE,
} from './gumroadBundleUiConfig';
import {
  buildGumroadBundleAdminPreviewDisplay,
  resolveDefaultAdminPreviewScenarioId,
} from './gumroadBundleAdminPreview';
import { GumroadBundleAdminPreviewToolbar } from './GumroadBundleAdminPreviewToolbar';
import {
  GumroadBundleActionRow,
  GumroadBundleHelpPanel,
  GumroadBundleLoadingState,
  GumroadBundleMarketplaceCta,
  GumroadBundlePromoCodeCard,
  GumroadBundleSectionIntro,
  GumroadBundleStatusPanel,
  GumroadBundleStepSection,
  GumroadBundleTransferHint,
} from './GumroadBundleUiPanels';
import './GumroadBundleSection.css';

export { GUMROAD_BUNDLE_UI_STATE };

function resolveMarketplaceUrl(status) {
  if (typeof window === 'undefined' || !status?.marketplaceDetailsPath) {
    return null;
  }
  return `${window.location.origin}${status.marketplaceDetailsPath}`;
}

function resolveRequestErrorMessage(data) {
  const code = data?.error || data?.code;
  if (code && GUMROAD_BUNDLE_REQUEST_ERROR_MESSAGES[code]) {
    return GUMROAD_BUNDLE_REQUEST_ERROR_MESSAGES[code];
  }
  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message.trim();
  }
  return GUMROAD_BUNDLE_REQUEST_ERROR_MESSAGES.gumroad_bundle_promo_request_failed;
}

function isKeyboardTypingTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
}

/**
 * @param {{
 *   uiState: string,
 *   status: object | null,
 *   errorMessage: string | null,
 *   onRequest: () => void,
 *   onRetry: () => void,
 *   requesting: boolean,
 *   onCopy: (code: string) => void,
 *   copied: boolean,
 *   previewMode?: boolean,
 *   onNavigateToTransfer?: () => void,
 * }} props
 */
function GumroadBundlePromoView({
  uiState,
  status,
  errorMessage,
  onRequest,
  onRetry,
  requesting,
  onCopy,
  copied,
  previewMode = false,
  onNavigateToTransfer,
}) {
  const marketplaceUrl = resolveMarketplaceUrl(status);
  const isTransferPendingFinalize = status?.code === 'transfer_pending_admin_finalize';

  if (uiState === GUMROAD_BUNDLE_UI_STATE.LOADING) {
    return (
      <div className="gumroad-bundle-detail-body">
        <GumroadBundleLoadingState />
      </div>
    );
  }

  return (
    <div className="gumroad-bundle-detail-body">
      <div className="gumroad-bundle-content-stack">
        <GumroadBundleSectionIntro uiState={uiState} status={status} />

      {uiState === GUMROAD_BUNDLE_UI_STATE.ERROR ? (
        <>
          <GumroadBundleStatusPanel
            variant="error"
            title={GUMROAD_BUNDLE_PANEL_COPY.errorTitle}
            lead={errorMessage || GUMROAD_BUNDLE_PANEL_COPY.errorFallback}
            role="alert"
          />
          {!previewMode ? (
            <GumroadBundleActionRow>
              <RevoltButton type="button" variant="primary" onClick={onRetry}>
                {GUMROAD_BUNDLE_PANEL_COPY.retryButton}
              </RevoltButton>
            </GumroadBundleActionRow>
          ) : null}
        </>
      ) : null}

      {uiState === GUMROAD_BUNDLE_UI_STATE.NOT_CONFIGURED ? (
        <GumroadBundleStatusPanel
          variant="neutral"
          title={GUMROAD_BUNDLE_PANEL_COPY.notConfiguredTitle}
          lead={GUMROAD_BUNDLE_PANEL_COPY.notConfiguredBody}
        />
      ) : null}

      {uiState === GUMROAD_BUNDLE_UI_STATE.ALREADY_ENTITLED ? (
        <GumroadBundleStatusPanel
          variant="success"
          title={GUMROAD_BUNDLE_PANEL_COPY.alreadyEntitledTitle}
          lead={GUMROAD_BUNDLE_PANEL_COPY.alreadyEntitledLead}
          role="status"
        >
          <p className="gumroad-bundle-status-panel__foot">
            {GUMROAD_BUNDLE_PANEL_COPY.alreadyEntitledFoot}
          </p>
        </GumroadBundleStatusPanel>
      ) : null}

      {uiState === GUMROAD_BUNDLE_UI_STATE.HAS_PROMO ? (
        <>
          <GumroadBundleStatusPanel
            variant="success"
            title={GUMROAD_BUNDLE_PANEL_COPY.hasPromoTitle}
            lead={GUMROAD_BUNDLE_PANEL_COPY.hasPromoLead}
            role="status"
          >
            <GumroadBundlePromoCodeCard
              code={status?.promo?.promotionCode ?? status?.promo?.code ?? ''}
              onCopy={onCopy}
              copied={copied}
            />
          </GumroadBundleStatusPanel>
          <GumroadBundleStepSection title="Next step">
            <GumroadBundleMarketplaceCta marketplaceUrl={marketplaceUrl} />
          </GumroadBundleStepSection>
          <GumroadBundleHelpPanel
            context={GUMROAD_BUNDLE_HELP_CONTEXT.CHECKOUT}
            marketplaceUrl={marketplaceUrl}
          />
        </>
      ) : null}

      {uiState === GUMROAD_BUNDLE_UI_STATE.ELIGIBLE ? (
        <>
          {(() => {
            const isTransferRecipient =
              status?.eligibilityPath === GUMROAD_BUNDLE_ELIGIBILITY_PATH.TRANSFER_RECIPIENT;
            const canRequestPromo = status?.canRequestPromo !== false;
            return (
              <>
                <GumroadBundleStatusPanel
                  variant="info"
                  title={
                    isTransferRecipient
                      ? GUMROAD_BUNDLE_PANEL_COPY.eligibleTransferTitle
                      : GUMROAD_BUNDLE_PANEL_COPY.eligibleGumroadTitle
                  }
                  lead={
                    isTransferRecipient
                      ? GUMROAD_BUNDLE_PANEL_COPY.eligibleTransferLead
                      : GUMROAD_BUNDLE_PANEL_COPY.eligibleGumroadLead
                  }
                  role="status"
                />
                {!canRequestPromo ? (
                  <GumroadBundleStatusPanel
                    variant="warning"
                    lead={GUMROAD_BUNDLE_ISSUANCE_PENDING_INTRO}
                    role="status"
                  />
                ) : null}
                {errorMessage ? (
                  <GumroadBundleStatusPanel variant="error" lead={errorMessage} role="alert" />
                ) : null}
                <GumroadBundleStepSection title="Request your code">
                  <GumroadBundleActionRow>
                    <RevoltButton
                      type="button"
                      variant="primary"
                      onClick={onRequest}
                      disabled={requesting || !canRequestPromo || previewMode}
                    >
                      {requesting
                        ? GUMROAD_BUNDLE_PANEL_COPY.requestButtonPending
                        : GUMROAD_BUNDLE_PANEL_COPY.requestButton}
                    </RevoltButton>
                  </GumroadBundleActionRow>
                </GumroadBundleStepSection>
              </>
            );
          })()}
        </>
      ) : null}

      {uiState === GUMROAD_BUNDLE_UI_STATE.NOT_ELIGIBLE ? (
        <>
          {isTransferPendingFinalize ? (
            <GumroadBundleStatusPanel
              variant="warning"
              title={GUMROAD_BUNDLE_PANEL_COPY.transferPendingTitle}
              lead={GUMROAD_BUNDLE_NOT_ELIGIBLE_INTRO.transfer_pending_admin_finalize}
              role="status"
            />
          ) : (
            <GumroadBundleStatusPanel
              variant="neutral"
              title={GUMROAD_BUNDLE_PANEL_COPY.notEligibleTitle}
              lead={
                GUMROAD_BUNDLE_NOT_ELIGIBLE_INTRO[status?.code] ||
                GUMROAD_BUNDLE_PANEL_COPY.notEligibleDefault
              }
            />
          )}
          {!isTransferPendingFinalize ? (
            <GumroadBundleTransferHint onNavigate={onNavigateToTransfer} />
          ) : null}
          <GumroadBundleHelpPanel
            context={
              isTransferPendingFinalize
                ? GUMROAD_BUNDLE_HELP_CONTEXT.TRANSFER_PENDING
                : GUMROAD_BUNDLE_HELP_CONTEXT.SUPPORT
            }
          />
        </>
      ) : null}
      </div>
    </div>
  );
}

/**
 * @param {{ user: object | null, onNavigateToTransfer?: () => void }} props
 */
export function GumroadBundleSection({ user, onNavigateToTransfer }) {
  const { manager: accountManager } = useManager(AccountManager);
  const isAdmin = user?.role === 'admin';

  const [uiState, setUiState] = useState(GUMROAD_BUNDLE_UI_STATE.LOADING);
  const [status, setStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [copied, setCopied] = useState(false);

  const [adminPreviewMode, setAdminPreviewMode] = useState(false);
  const [previewScenarioId, setPreviewScenarioId] = useState('already_entitled');

  const loadStatus = useCallback(async () => {
    if (!user?.id) return;
    setUiState(GUMROAD_BUNDLE_UI_STATE.LOADING);
    setErrorMessage(null);
    try {
      const data = await accountManager.fetchGumroadBundlePromoStatus();
      setStatus(data);
      setUiState(data?.eligibilityState ?? GUMROAD_BUNDLE_UI_STATE.ERROR);
    } catch (error) {
      setErrorMessage(error?.message || 'Could not load bundle promo status.');
      setUiState(GUMROAD_BUNDLE_UI_STATE.ERROR);
    }
  }, [accountManager, user?.id]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const previewDisplay = useMemo(() => {
    if (!adminPreviewMode) return null;
    return buildGumroadBundleAdminPreviewDisplay(previewScenarioId, {
      marketplaceDetailsPath: status?.marketplaceDetailsPath,
    });
  }, [adminPreviewMode, previewScenarioId, status?.marketplaceDetailsPath]);

  const displayUiState = previewDisplay?.uiState ?? uiState;
  const displayStatus = previewDisplay?.status ?? status;
  const displayErrorMessage = previewDisplay?.errorMessage ?? errorMessage;

  const handleToggleAdminPreview = useCallback(() => {
    setAdminPreviewMode((on) => {
      if (!on) {
        setPreviewScenarioId(resolveDefaultAdminPreviewScenarioId(uiState));
      }
      return !on;
    });
  }, [uiState]);

  useEffect(() => {
    if (!isAdmin) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== 'h' && event.key !== 'H') return;
      if (event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isKeyboardTypingTarget(event.target)) return;

      event.preventDefault();
      handleToggleAdminPreview();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isAdmin, handleToggleAdminPreview]);

  const handleCopy = useCallback(async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setErrorMessage('Could not copy to clipboard. Select the code and copy manually.');
    }
  }, []);

  const handleRequest = useCallback(async () => {
    if (adminPreviewMode) return;
    setRequesting(true);
    setErrorMessage(null);
    try {
      const { ok, data } = await accountManager.requestGumroadBundlePromo();
      if (ok && data?.success) {
        await loadStatus();
        return;
      }
      if (data?.promo) {
        setStatus((prev) => ({
          ...(prev || {}),
          promo: data.promo,
          eligibilityState: GUMROAD_BUNDLE_UI_STATE.HAS_PROMO,
        }));
        setUiState(GUMROAD_BUNDLE_UI_STATE.HAS_PROMO);
        return;
      }
      setErrorMessage(resolveRequestErrorMessage(data));
      setUiState(GUMROAD_BUNDLE_UI_STATE.ELIGIBLE);
    } catch (error) {
      setErrorMessage(error?.message || 'Could not generate a promotion code.');
      setUiState(GUMROAD_BUNDLE_UI_STATE.ERROR);
    } finally {
      setRequesting(false);
    }
  }, [accountManager, adminPreviewMode, loadStatus]);

  return (
    <div className="gumroad-bundle-section">
      <div className="account-card">
        <div className="card-header">
          <h2 className="card-title">
            <i className="fa-solid fa-store" aria-hidden />
            Gumroad bundle access
          </h2>
        </div>
        <div className="card-body">
          {isAdmin && adminPreviewMode ? (
            <>
              <p className="gumroad-bundle-admin-banner" role="status">
                Admin preview — mock scenario:{' '}
                <strong>{previewScenarioId.replace(/_/g, ' ')}</strong>
              </p>
              <GumroadBundleAdminPreviewToolbar
                scenarioId={previewScenarioId}
                onScenarioChange={setPreviewScenarioId}
              />
            </>
          ) : null}
          <GumroadBundlePromoView
            uiState={displayUiState}
            status={displayStatus}
            errorMessage={displayErrorMessage}
            onRequest={handleRequest}
            onRetry={loadStatus}
            requesting={requesting}
            onCopy={handleCopy}
            copied={copied}
            previewMode={adminPreviewMode}
            onNavigateToTransfer={onNavigateToTransfer}
          />
        </div>
      </div>
    </div>
  );
}
